import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ScrollText, X, CheckCircle2 } from "lucide-react";

export function useTournamentWaiver(tid) {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setW(null);
    if (!tid) return;
    api.get(`/tournaments/${tid}/waiver`).then(({ data }) => setW(data)).catch(() => setW({ error: true }));
  }, [tid]);
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${tid}/waiver/accept`);
      setW((x) => ({ ...(x || {}), signed: true, signed_at: data.signed_at }));
      toast.success("Waiver signed — you're cleared to register");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return { waiver: w, signed: !!w?.signed, accept, busy };
}

export function WaiverModal({ waiver, eventName, onClose, onAgree, signed }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="waiver-modal-backdrop">
      <div className="bg-pb-surface border border-amber-400 w-full max-w-3xl max-h-[92vh] flex flex-col animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="waiver-modal">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-pb-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold"><ScrollText className="w-3.5 h-3.5" /> Required before checkout</div>
            <h3 className="font-display font-black text-base sm:text-lg uppercase leading-tight mt-1" data-testid="waiver-title">{waiver?.title}</h3>
            <div className="text-xs text-zinc-400 mt-1" data-testid="waiver-event-name">
              Event: <span className="text-white font-bold">{eventName}</span>
              {waiver?.tournament?.date ? ` · ${waiver.tournament.date}` : ""}{waiver?.tournament?.location ? ` · ${waiver.tournament.location}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white shrink-0" data-testid="waiver-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto overscroll-contain text-sm text-zinc-300 leading-relaxed space-y-5" data-testid="waiver-body">
          {(waiver?.sections || []).map((s) => (
            <section key={s.heading}>
              <h4 className="font-display font-black uppercase text-white text-sm tracking-wide mb-1.5">{s.heading}</h4>
              {s.paragraphs.map((p, i) => <p key={i} className="mb-2">{p}</p>)}
            </section>
          ))}
          {waiver?.also_signed?.length > 0 && (
            <div className="border border-pb-border bg-pb-elevated/40 p-3 text-xs" data-testid="waiver-also-signed">
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Waivers on file for your other events</div>
              {waiver.also_signed.map((o, i) => <div key={i} className="text-zinc-400">✓ {o.tournament_name} <span className="text-zinc-600">· {new Date(o.accepted_at).toLocaleDateString()}</span></div>)}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-pb-border shrink-0 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="text-[11px] text-zinc-500 flex-1">
            By checking the box you electronically sign this agreement for <span className="text-white font-bold">{eventName}</span>. Your name, email and time of signature are recorded.
          </div>
          {signed ? (
            <div className="inline-flex items-center gap-1.5 text-emerald-300 text-xs font-bold uppercase tracking-widest" data-testid="waiver-modal-signed"><CheckCircle2 className="w-4 h-4" /> Signed</div>
          ) : (
            <button data-testid="waiver-modal-agree" onClick={onAgree}
              className="bg-amber-400 text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-amber-300 active:scale-95 transition-all">
              I have read & agree
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function WaiverGate({ tid, eventName, testid = "waiver-gate", onChange }) {
  const { waiver, signed, accept, busy } = useTournamentWaiver(tid);
  const [open, setOpen] = useState(false);
  useEffect(() => { onChange?.(signed); }, [signed]); // eslint-disable-line
  const agree = async () => { await accept(); setOpen(false); };
  return (
    <>
      <label data-testid={testid}
        className={`flex items-start gap-3 border px-3 py-3 cursor-pointer transition-colors ${signed ? "border-emerald-500/60 bg-emerald-500/5" : "waiver-glow border-amber-400 bg-amber-400/5"}`}>
        <input type="checkbox" checked={signed} disabled={signed || busy} onChange={(e) => { if (e.target.checked) accept(); }}
          className="accent-[#FACC15] w-5 h-5 mt-0.5 shrink-0" data-testid={`${testid}-checkbox`} />
        <span className="flex-1 min-w-0">
          <span className={`block text-sm font-bold ${signed ? "text-emerald-300" : "text-amber-200"}`}>
            {signed ? <><ShieldCheck className="inline w-4 h-4 mr-1 -mt-0.5" />Waiver signed for {eventName}</> : <>I have read and agree to the Tournament Liability Waiver for <span className="text-white">{eventName}</span> <span className="text-amber-400">*</span></>}
          </span>
          <span className="block text-[10px] text-zinc-500 mt-0.5">
            {signed ? `Signed ${waiver?.signed_at ? new Date(waiver.signed_at).toLocaleString() : ""}` : "Required — you can't check out or pay until this is signed."}
            {" "}
            <button type="button" data-testid={`${testid}-view`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
              className="underline underline-offset-2 text-amber-300 hover:text-amber-200 font-bold">Read the waiver</button>
          </span>
        </span>
      </label>
      {open && <WaiverModal waiver={waiver} eventName={eventName} signed={signed} onClose={() => setOpen(false)} onAgree={agree} />}
    </>
  );
}
